import { useEffect, useState, useMemo } from "react";
import { inr } from "@/lib/format";
import Modal, { inputCls, btnPrimary, btnGhost } from "@/components/Modal";
import { Banknote, Smartphone, CreditCard, Split, Check } from "lucide-react";

const METHODS = [
  { id: "CASH", label: "Cash", icon: Banknote, key: "1" },
  { id: "UPI", label: "UPI", icon: Smartphone, key: "2" },
  { id: "CARD", label: "Card", icon: CreditCard, key: "3" },
  { id: "SPLIT", label: "Split", icon: Split, key: "4" },
];

export default function PaymentModal({ open, onClose, total, onConfirm }) {
  const [method, setMethod] = useState("CASH");
  const [cash, setCash] = useState("");
  const [upiRef, setUpiRef] = useState("");
  const [split, setSplit] = useState({ CASH: "", UPI: "", CARD: "" });

  useEffect(() => {
    if (open) {
      setMethod("CASH");
      setCash(String(Math.round(total)));
      setUpiRef("");
      setSplit({ CASH: "", UPI: "", CARD: "" });
    }
  }, [open, total]);

  const change = useMemo(() => Math.max(0, (parseFloat(cash) || 0) - total), [cash, total]);
  const splitTotal = useMemo(
    () => Object.values(split).reduce((a, b) => a + (parseFloat(b) || 0), 0),
    [split]
  );

  const canConfirm = useMemo(() => {
    if (method === "CASH") return (parseFloat(cash) || 0) >= total;
    if (method === "SPLIT") return Math.abs(splitTotal - total) < 0.5;
    return true;
  }, [method, cash, split, splitTotal, total]);

  const confirm = () => {
    if (!canConfirm) return;
    let payment = { method };
    if (method === "CASH") payment = { method, cash_received: parseFloat(cash), change };
    if (method === "UPI") payment = { method, upi_ref: upiRef };
    if (method === "SPLIT") {
      payment = {
        method,
        splits: Object.entries(split)
          .filter(([, v]) => parseFloat(v) > 0)
          .map(([k, v]) => ({ method: k, amount: parseFloat(v) })),
      };
    }
    onConfirm(payment);
  };

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      const m = METHODS.find((x) => x.key === e.key);
      if (m && document.activeElement?.tagName !== "INPUT") setMethod(m.id);
      if (e.key === "Enter" && canConfirm) {
        e.preventDefault();
        confirm();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, canConfirm, method, cash, split, upiRef]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Payment"
      width="max-w-md"
      footer={
        <>
          <button onClick={onClose} className={btnGhost}>Cancel</button>
          <button onClick={confirm} disabled={!canConfirm} data-testid="confirm-payment-btn" className={btnPrimary + " flex items-center gap-2"}>
            <Check className="w-4 h-4" /> Complete Payment
          </button>
        </>
      }
    >
      <div className="text-center mb-5">
        <div className="text-xs text-[#6B7280] uppercase tracking-wide">Amount Payable</div>
        <div className="font-head font-bold text-4xl font-mono text-[#10B981] mt-1">{inr(total)}</div>
      </div>

      <div className="grid grid-cols-4 gap-2 mb-5">
        {METHODS.map((m) => (
          <button
            key={m.id}
            data-testid={`pay-method-${m.id}`}
            onClick={() => setMethod(m.id)}
            className={`flex flex-col items-center gap-1.5 py-3 rounded-xl border transition-colors ${
              method === m.id ? "bg-[#10B981] border-[#10B981] text-[#06221a]" : "bg-[#0B0F17] border-[#23334D] text-[#9CA3AF] hover:border-[#10B981]/50"
            }`}
          >
            <m.icon className="w-5 h-5" />
            <span className="text-xs font-semibold">{m.label}</span>
            <span className={`kbd ${method === m.id ? "opacity-0" : ""}`}>{m.key}</span>
          </button>
        ))}
      </div>

      {method === "CASH" && (
        <div className="space-y-3">
          <div>
            <label className="text-xs font-semibold text-[#9CA3AF] uppercase tracking-wide">Cash Received</label>
            <input autoFocus type="number" className={inputCls + " mt-1.5 text-lg font-mono"} value={cash} data-testid="cash-received-input" onChange={(e) => setCash(e.target.value)} />
          </div>
          <div className="flex gap-2">
            {[100, 200, 500, 1000].map((n) => (
              <button key={n} onClick={() => setCash(String(n))} className="flex-1 py-2 rounded-lg bg-[#1E2C45] text-sm hover:bg-[#263657]">₹{n}</button>
            ))}
          </div>
          <div className="flex justify-between items-center bg-[#0B0F17] border border-[#23334D] rounded-lg px-4 py-3">
            <span className="text-[#9CA3AF]">Change to return</span>
            <span className="font-head font-bold text-xl font-mono text-[#F59E0B]" data-testid="change-amount">{inr(change)}</span>
          </div>
        </div>
      )}

      {method === "UPI" && (
        <div>
          <label className="text-xs font-semibold text-[#9CA3AF] uppercase tracking-wide">UPI Reference (optional)</label>
          <input className={inputCls + " mt-1.5"} value={upiRef} onChange={(e) => setUpiRef(e.target.value)} placeholder="Transaction ID" autoFocus />
          <p className="text-sm text-[#6B7280] mt-3">Press Enter to mark as paid.</p>
        </div>
      )}

      {method === "CARD" && <p className="text-sm text-[#6B7280] text-center py-4">Swipe/insert card on the machine, then press Enter to mark as paid.</p>}

      {method === "SPLIT" && (
        <div className="space-y-3">
          {["CASH", "UPI", "CARD"].map((k) => (
            <div key={k} className="flex items-center gap-3">
              <span className="w-16 text-sm text-[#9CA3AF]">{k}</span>
              <input type="number" className={inputCls + " font-mono"} value={split[k]} data-testid={`split-${k}`} onChange={(e) => setSplit({ ...split, [k]: e.target.value })} placeholder="0" />
            </div>
          ))}
          <div className={`flex justify-between px-4 py-2.5 rounded-lg ${Math.abs(splitTotal - total) < 0.5 ? "bg-[#10B981]/10 text-[#10B981]" : "bg-[#EF4444]/10 text-[#EF4444]"}`}>
            <span>Entered</span>
            <span className="font-mono">{inr(splitTotal)} / {inr(total)}</span>
          </div>
        </div>
      )}
    </Modal>
  );
}
